/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { bindActionCreators, compose, Dispatch } from "redux";

import { Stack } from "office-ui-fabric-react/lib";

import { ActionButton, Text } from "@fluentui/react";

import * as AccessControlActions from "Dataflows/AccessControl/AccessControlAction";
import * as AccessControlSelectors from "Dataflows/AccessControl/AccessControlSelector";
import * as ClRegionActions from "Dataflows/ClRegion/ClRegionAction";
import * as ClRegionSelector from "Dataflows/ClRegion/ClRegionSelector";
import * as TechnologyFocusAreaActions from "Dataflows/TechnologyArea/TechnologyAreaAction";
import * as TechnologyFocusAreaSelectors from "Dataflows/TechnologyArea/TechnologyAreaSelector";

import AccessControlRoles from "Components/AccessControlRoles/AccessControlRoles";
import AccessControlUserForm from "Components/AccessControlUserForm/AccessControlUserForm";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import {
  listStackStyles,
  tableStackStyles,
  Divider,
} from "Containers/Common/CommonContainer.styles";
import { AppState } from "Dataflows/Reducers";

import { innerStackStyles } from "./AccessControlCreateEditContainer.styles";
import { IAccessControlCreateEditContainerProps } from "./IAccessControlCreateEditContainerProps";
import {
  ContainerWrapper,
  headerStackStyles,
  headerTextStyles,
} from "Components/Common/SharedTableStyles.styles";

/**
 * AccessControl Create Edit Container
 * @returns {React.ReactElement} rendered create edit access container for user
 */
const AccessControlCreateEditContainer: React.FunctionComponent<
  IAccessControlCreateEditContainerProps
> = (props: IAccessControlCreateEditContainerProps): React.ReactElement => {
  const {
    history,
    match: { params },
    selectedUser,
    selectedUserId,
    actions,
    roles,
    technologyFocusAreas,
    geoData,
    networkOperationStatus,
    networkOperationError,
  } = props;

  const tenant = SubSiteUrlRootPath[params.tenant];
  const isEditing = selectedUserId !== 0;
  if (!tenant) {
    history.push("/notFound");
  }

  /**
   * Handler for when the back button is clicked.
   * @returns {void} void no return for function.
   */
  const onBackClickEvent = (): void => {
    actions.resetProps();
    actions.updateSelectedUserId(0);
    history.goBack();
  };

  useEffect(() => {
    if ([TenantTypesEnum.MVP, TenantTypesEnum.RD].includes(tenant)) {
      actions.loadTechFocusAreas(tenant);
    }

    actions.loadGeoData();

    if (isEditing) {
      const adminUserEmail = selectedUser?.emailAddress ?? "";
      actions.loadUserRoles(tenant, adminUserEmail);
    }

    return (): void => {
      actions.resetProps();
    };
  }, []);

  return (
    <Stack>
      <ActionButton
        iconProps={{ iconName: "RevToggleKey" }}
        ariaLabel="Back to Access Control"
        onClick={onBackClickEvent}
      >
        Back to Access Control
      </ActionButton>

      <ContainerWrapper>
        <Stack styles={innerStackStyles}>
          <ContainerWrapper>
            <Stack styles={headerStackStyles}>
              <Text styles={headerTextStyles}>{isEditing ? "Edit User" : "Add new user"}</Text>
            </Stack>
            <AccessControlUserForm tenant={tenant} isEditing={isEditing} {...props} />
          </ContainerWrapper>
          {isEditing ? (
            <Stack.Item grow>
              <Stack styles={tableStackStyles}>
                <AccessControlRoles
                  isLoadingStatus={networkOperationStatus}
                  roles={roles}
                  tenant={tenant}
                  selectedUser={selectedUser}
                  technologyFocusAreas={technologyFocusAreas}
                  geoData={geoData}
                  actions={{
                    onCreateUserRole: actions.createUserRole,
                    onUpdateUserRole: actions.updateUserRole,
                    onDeleteUserRole: actions.deleteUserRole,
                  }}
                />
              </Stack>
            </Stack.Item>
          ) : null}
        </Stack>
      </ContainerWrapper>
    </Stack>
  );
};

/**
 * Maps AccessControlCreateEditContainer to redux state
 * @param {AppState} state The App state.
 * @returns {Partial<IAccessControlCreateEditContainerProps>} AccessControlCreateEdit container state mapped to props
 */
const mapStateToProps = (state: AppState): Partial<IAccessControlCreateEditContainerProps> => ({
  selectedUserId: AccessControlSelectors.getSelectedUserId(state.accessControlState),
  selectedUser: AccessControlSelectors.getSelectedUser(state.accessControlState),
  newUserId: AccessControlSelectors.getNewUserId(state.accessControlState),
  roles: AccessControlSelectors.getUserRoles(state.accessControlState),
  networkOperationStatus: AccessControlSelectors.getNetworkOperationStatus(
    state.accessControlState
  ),
  networkOperationError: AccessControlSelectors.getNetworkOperationError(state.accessControlState),
  technologyFocusAreas: TechnologyFocusAreaSelectors.getTechnologyAreaOptionList(
    state.technologyAreaDataState
  ),
  geoData: ClRegionSelector.getGeoData(state.clRegionsDataState),
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch<AccessControlActions.AccessControlActionTypes>} dispatch map dispatch to props
 * @returns {Partial<IAccessControlCreateEditContainerProps>} map dispatch to props
 */
const mapDispatchToProps = (
  dispatch: Dispatch<AccessControlActions.AccessControlActionTypes>
): Partial<IAccessControlCreateEditContainerProps> => ({
  actions: {
    resetProps: bindActionCreators(AccessControlActions.resetProps, dispatch),
    updateSelectedUserId: bindActionCreators(AccessControlActions.updateSelectedUserId, dispatch),
    updateSelectedUser: bindActionCreators(AccessControlActions.updateSelectedUser, dispatch),
    loadUserRoles: bindActionCreators(AccessControlActions.loadUserRoles, dispatch),
    createUserRole: bindActionCreators(AccessControlActions.createUserRole, dispatch),
    updateUserRole: bindActionCreators(AccessControlActions.updateUserRole, dispatch),
    deleteUserRole: bindActionCreators(AccessControlActions.deleteUserRole, dispatch),
    loadTechFocusAreas: bindActionCreators(TechnologyFocusAreaActions.getTechnologyAreas, dispatch),
    loadGeoData: bindActionCreators(ClRegionActions.getGeoData, dispatch),
  },
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withRouter, withConnect)(AccessControlCreateEditContainer) as React.FC<any>;
